/* global document, window, FileReader, Image */
// Web stand-in for react-native-image-picker. Opens the OS file/camera picker
// via a hidden <input type="file"> and returns the same shape the native module
// does: { assets: [{ uri, base64, fileName, type, fileSize, width, height }] }
// or { didCancel: true }.

// Downscale to <= maxDim and re-encode as JPEG so the base64 payload stays
// small enough for the command body.
function downscale(dataUrl, maxDim, quality) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function fileToAsset(file, opts = {}) {
  const maxDim = Math.max(opts.maxWidth || 0, opts.maxHeight || 0) || 1280;
  const quality = typeof opts.quality === 'number' ? opts.quality : 0.65;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async () => {
      let dataUrl = String(reader.result);
      if ((file.type || '').startsWith('image/')) {
        try { dataUrl = await downscale(dataUrl, maxDim, quality); } catch { /* keep original */ }
      }
      resolve({
        uri: dataUrl,
        base64: dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : undefined,
        fileName: (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg',
        type: 'image/jpeg',
        fileSize: file.size,
      });
    };
    reader.onerror = () => resolve({ uri: undefined, fileName: file.name, type: file.type });
    reader.readAsDataURL(file);
  });
}

export function launchImageLibrary(options = {}, callback) {
  const promise = new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = options.mediaType === 'video' ? 'video/*' : 'image/*';
    if ((options.selectionLimit ?? 1) !== 1) input.multiple = true;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);

    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      input.remove();
      if (typeof callback === 'function') callback(result);
      resolve(result);
    };

    input.addEventListener('change', async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return finish({ didCancel: true });
      const assets = await Promise.all(files.map((f) => fileToAsset(f, options)));
      finish({ assets });
    });
    // Best-effort cancel detection when the dialog is dismissed.
    window.addEventListener(
      'focus',
      () => setTimeout(() => { if (!input.files || !input.files.length) finish({ didCancel: true }); }, 800),
      { once: true },
    );

    input.click();
  });
  return promise;
}

export function launchCamera(options = {}, callback) {
  return launchImageLibrary({ ...options, cameraCapture: true }, callback);
}

export default { launchImageLibrary, launchCamera };
